import { parseBarcodes } from '../../domain/stock';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockApi } from '../../api/stock-api';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  stockUnitLabels,
  stockUnits,
  stockUsageLabels,
  stockUsages,
  type StockItem,
  type StockUnit,
  type StockUsage,
} from '../../domain/fulfillment';
import { isUnknownOutcome, stockErrorMessage } from './error-message';
import { StockItemPhotoField } from './StockItemPhotoField';

const unitNames: Record<StockUnit, string> = {
  PIECE: 'Pieces',
  METER: 'Meters (cable and other lengths)',
  CENTIMETER: 'Centimeters (cut to length)',
  GRAM: 'Grams',
  MILLILITER: 'Milliliters',
};

/** Creates or edits a stock catalog item. Items are deactivated, never deleted. */
export function StockItemDrawer({
  item,
  onClose,
}: {
  item?: StockItem | undefined;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // A photo saves on its own and bumps the version; keep the latest so
  // Save doesn't conflict with it.
  const [current, setCurrent] = useState(item);
  const [sku, setSku] = useState(item?.sku ?? '');
  const [displayName, setDisplayName] = useState(item?.displayName ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [unit, setUnit] = useState<StockUnit>(item?.stockUnit ?? 'PIECE');
  // Changing the unit doesn't convert stock already counted
  // (tidaltech/koi#8466): say so while it would matter.
  const unitChanged = item !== undefined && unit !== item.stockUnit;
  const balances = useQuery({
    queryKey: ['stock-balances', 'item', item?.id],
    queryFn: () => stockApi.listStockBalances({ stockItemId: item?.id }),
    enabled: unitChanged,
  });
  const onHand = (balances.data ?? []).reduce((total, balance) => total + balance.onHand, 0);
  const [active, setActive] = useState(item?.active ?? true);
  const [barcodes, setBarcodes] = useState((item?.barcodes ?? []).join('\n'));
  const [usage, setUsage] = useState<StockUsage | ''>(item?.usage ?? '');
  const [minimum, setMinimum] = useState(item?.minimumQuantity ? String(item.minimumQuantity) : '');
  const [error, setError] = useState<string | null>(null);
  const [unknown, setUnknown] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const input = {
        sku,
        displayName,
        // On update '' clears it; an omitted description leaves it unchanged.
        description: item ? description.trim() : description.trim() || undefined,
        stockUnit: unit,
        active,
        barcodes: parseBarcodes(barcodes),
        usage,
        minimumQuantity: minimum.trim() ? Number(minimum) : 0,
      };
      return current
        ? stockApi.updateStockItem(current.id, current.version, input)
        : stockApi.createStockItem(input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stock'] });
      await queryClient.invalidateQueries({ queryKey: ['stock-items'] });
      onClose();
    },
    onError: (failure: unknown) => {
      if (isUnknownOutcome(failure)) setUnknown(true);
      else
        setError(
          stockErrorMessage(failure, 'The stock item could not be saved.', 'stock item (SKU)'),
        );
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    if (!sku.trim() || !displayName.trim()) {
      setError('A SKU and a name are required.');
      return;
    }
    if (minimum.trim() && !/^\d+$/.test(minimum.trim())) {
      setError('The minimum quantity must be a whole number, 0 or more.');
      return;
    }
    save.mutate();
  }

  const title = item ? `Edit ${item.sku}` : 'New stock item';
  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close stock item editor"
          >
            ×
          </button>
        </div>
        {error ? (
          <p role="alert" className="form-error">
            {error}
          </p>
        ) : null}
        {unknown ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: ['stock'] });
              onClose();
            }}
          />
        ) : null}
        <label>
          SKU
          <input
            value={sku}
            onChange={(event) => setSku(event.target.value)}
            placeholder="e.g. ART-10001"
          />
        </label>
        <p className="field-hint">Stored in capitals; two items can’t share a SKU.</p>
        <label>
          Name
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="e.g. M12 connector plug"
          />
        </label>
        <label>
          Barcodes
          <textarea
            value={barcodes}
            onChange={(event) => setBarcodes(event.target.value)}
            rows={2}
            placeholder="e.g. 7038010012345"
          />
        </label>
        <p className="field-hint">
          The codes printed on the item (EAN, or an internal one), one per line. Scanning one finds
          this item like its SKU; a barcode can belong to only one item.
        </p>
        <label>
          Description
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={2}
          />
        </label>
        {current ? (
          <StockItemPhotoField item={current} onChanged={setCurrent} />
        ) : (
          <p className="field-hint">You can add a photo once the item is created.</p>
        )}
        <label>
          Used for
          <SearchableSelect
            ariaLabel="Used for"
            value={usage}
            onChange={(value) => setUsage(value as StockUsage | '')}
            options={[
              { value: '', label: 'Not set' },
              ...stockUsages.map((candidate) => ({
                value: candidate,
                label: stockUsageLabels[candidate],
              })),
            ]}
            placeholder="Not set"
          />
        </label>
        <p className="field-hint">
          Internal items are used by Tidal itself (tools, laptops); customer consumables go out to
          sites.
        </p>
        <label>
          Minimum quantity
          <input
            inputMode="numeric"
            value={minimum}
            onChange={(event) => setMinimum(event.target.value)}
            placeholder="No minimum"
          />
        </label>
        <p className="field-hint">
          Stock below this, in the unit it’s counted in, is flagged for reordering. Leave empty for
          none.
        </p>
        <label>
          Counted in
          <SearchableSelect
            ariaLabel="Counted in"
            value={unit}
            onChange={(value) => setUnit(value as StockUnit)}
            options={stockUnits.map((candidate) => ({
              value: candidate,
              label: `${unitNames[candidate]} (${stockUnitLabels[candidate]})`,
            }))}
            placeholder="Pieces"
          />
        </label>
        {unitChanged && item && onHand > 0 ? (
          <p className="warning-callout" role="alert">
            {onHand.toLocaleString()} {stockUnitLabels[item.stockUnit]} are on hand. Changing the
            unit keeps the numbers as they are: {onHand.toLocaleString()}{' '}
            {stockUnitLabels[item.stockUnit]} becomes {onHand.toLocaleString()}{' '}
            {stockUnitLabels[unit]}. If the count is different in the new unit, adjust the stock
            after saving.
          </p>
        ) : null}
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span>Active (can be received and ordered)</span>
        </label>
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : item ? 'Save' : 'Create stock item'}
          </button>
        </div>
      </form>
    </div>
  );
}
